import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spinner, Button } from './UI';
import SafeHtml from './SafeHtml';
import ResumeButton from './ResumeButton';
import { candidateAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fmtExp, fmtLPA } from '../utils/format';

// ── Small presentational helpers (theme-agnostic via CSS vars) ────────────────
function Section({ title, icon, children }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 14, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        background: 'var(--bg-raised)',
      }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <h3 style={{
          fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', margin: 0,
          textTransform: 'uppercase', letterSpacing: '0.05em',
          wordBreak: 'break-word', minWidth: 0, overflowWrap: 'anywhere',
        }}>{title}</h3>
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

// Renders nothing when the value is empty (but keeps 0 / "Immediate").
function InfoRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 9 }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 108, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (isNaN(t.getTime())) return null;
  return t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtNotice = (n, serving) => {
  if (n === null || n === undefined || n === '') return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return `${n}`;
  const base = num === 0 ? 'Immediate' : `${num} days`;
  return serving ? `${base} · currently serving` : base;
};

// A Section that only renders if it has at least one non-null child value.
// We render children and check — simpler: caller passes a `has` boolean.
function MaybeSection({ has, ...rest }) {
  if (!has) return null;
  return <Section {...rest} />;
}

/**
 * CandidateDetailsModal — reusable read-only popup showing ALL candidate details.
 *
 * Fetches the full candidate profile on open (candidateAPI.getFullProfile) so it
 * shows every field, not just the subset a submission/interview payload carries.
 * `candidateFallback` (the subset already in hand) is shown instantly while the
 * full record loads, and is the graceful degradation if the fetch is forbidden
 * (403) or fails.
 *
 * Props:
 *   candidateId       — required, the candidate to load
 *   candidateFallback — optional subset (e.g. submission.candidate) for instant paint
 *   isOpen            — defaults true (callers usually conditional-render this)
 *   onClose           — required
 */
export default function CandidateDetailsModal({ candidateId, candidateFallback = null, isOpen = true, onClose }) {
  const { isDeveloper } = useAuth();
  const [candidate, setCandidate] = useState(candidateFallback);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(false);
  const fetchedIdRef = useRef(null);
  // Guards every async setState (effect-triggered AND retry-triggered) against
  // firing after the modal unmounts.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Single fetch path shared by the open-effect and the Retry button.
  const fetchProfile = useCallback(() => {
    if (!candidateId) return;
    setLoading(true);
    setError(false);
    candidateAPI.getFullProfile(candidateId)
      .then(({ data }) => {
        if (!mountedRef.current) return;
        if (data?.candidate) {
          setCandidate(data.candidate);
          fetchedIdRef.current = candidateId;
        }
      })
      .catch(() => {
        if (!mountedRef.current) return;
        // If we have a fallback, silently keep showing it (e.g. 403 for a role
        // without full-profile access). Only surface an error with nothing to show.
        if (!candidateFallback) setError(true);
      })
      .finally(() => { if (mountedRef.current) setLoading(false); });
  }, [candidateId, candidateFallback]);

  useEffect(() => {
    if (!isOpen || !candidateId) return;
    // Already loaded the full record for this id — do nothing (don't re-seed the
    // fallback subset over the full data on an incidental parent re-render).
    if (fetchedIdRef.current === candidateId) return;
    if (candidateFallback) setCandidate(candidateFallback);
    fetchProfile();
    // Intentionally keyed only on open + id: candidateFallback/fetchProfile
    // identity changes must not re-trigger a fetch or a fallback re-seed.
  }, [isOpen, candidateId]);

  const c = candidate || {};

  // Salary visibility mirrors the CandidateProfile / tile policy: hidden from the
  // Developer role unless the candidate was itself created by a Developer.
  const canSeeSalary = !isDeveloper || c.createdByRole === 'Developer';
  const curSalary = canSeeSalary ? fmtLPA(c.currentSalary) : null;
  const expSalary = canSeeSalary ? fmtLPA(c.expectedSalary) : null;

  const skills = Array.isArray(c.skills) ? c.skills.filter(Boolean) : [];
  const notice = fmtNotice(c.noticePeriod, c.servingNoticePeriod);

  const hasContact      = !!(c.email || c.phone || c.location || c.preferredLocation || c.linkedInUrl);
  const hasProfessional = !!(c.currentRole || c.currentCompany || c.experience || c.relevantExperience || skills.length);
  const hasComp         = !!(curSalary || expSalary);
  const hasAvailability = !!(notice || c.noticePeriodNotes || c.lastWorkingDate || c.offerInHand || c.offerCompany || c.offerPackage || c.offerDoj);
  const hasNotes        = !!(c.notes || c.clientNotes);
  const hasExpSummary   = !!c.experienceSummary;
  const anySection      = hasContact || hasProfessional || hasComp || hasAvailability || hasNotes || hasExpSummary;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={c.fullName ? `${c.fullName}` : 'Candidate Details'} size="lg">
      {/* Sub-header: email + loading hint */}
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginTop: -4, marginBottom: 14 }}>
        <p className="break-all" style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, overflowWrap: 'anywhere' }}>
          {c.email || '—'}
        </p>
        {loading && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Loading full profile…</span>}
      </div>

      {error && !candidate ? (
        <div className="text-center" style={{ padding: '32px 16px' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 12 }}>Couldn't load candidate details.</p>
          <Button variant="secondary" size="sm" onClick={fetchProfile}>
            Retry
          </Button>
        </div>
      ) : loading && !candidate ? (
        <div style={{ padding: '40px 0' }}><Spinner /></div>
      ) : !anySection ? (
        <div className="text-center" style={{ padding: '32px 16px', color: 'var(--text-muted)', fontSize: 14 }}>
          No additional details recorded for this candidate.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 14 }}>

          <MaybeSection has={hasContact} title="Contact" icon="👤">
            <InfoRow label="Email"    value={c.email} />
            <InfoRow label="Phone"    value={c.phone && <a href={`tel:${c.phone}`} style={{ color: 'var(--brand-primary)' }}>{c.phone}</a>} />
            <InfoRow label="Location" value={c.location} />
            <InfoRow label="Preferred" value={c.preferredLocation} />
            <InfoRow label="LinkedIn" value={c.linkedInUrl && (
              <a href={c.linkedInUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)', textDecoration: 'underline' }}>
                {c.linkedInUrl}
              </a>
            )} />
          </MaybeSection>

          <MaybeSection has={hasProfessional} title="Professional" icon="💼">
            <InfoRow label="Current Role"    value={c.currentRole} />
            <InfoRow label="Current Company" value={c.currentCompany} />
            <InfoRow label="Experience"      value={fmtExp(c.experience)} />
            <InfoRow label="Relevant Exp."   value={fmtExp(c.relevantExperience) || (typeof c.relevantExperience === 'string' ? c.relevantExperience : null)} />
            {skills.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Skills</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {skills.map(s => (
                    <span key={s} style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99,
                      background: 'var(--bg-raised)', color: 'var(--text-secondary)',
                      border: '1px solid var(--border-subtle)', overflowWrap: 'anywhere',
                    }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
          </MaybeSection>

          <MaybeSection has={hasComp} title="Compensation" icon="💰">
            <InfoRow label="Current CTC"  value={curSalary ? `${curSalary} / yr` : null} />
            <InfoRow label="Expected CTC" value={expSalary ? `${expSalary} / yr` : null} />
          </MaybeSection>

          <MaybeSection has={hasAvailability} title="Availability & Offer" icon="📅">
            <InfoRow label="Notice Period" value={notice} />
            {c.noticePeriodNotes && (
              <div style={{ margin: '4px 0 8px' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notice Period Notes</span>
                <SafeHtml html={c.noticePeriodNotes} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }} />
              </div>
            )}
            <InfoRow label="Last Working"  value={fmtDate(c.lastWorkingDate)} />
            <InfoRow label="Offer in Hand" value={c.offerInHand ? 'Yes' : null} />
            <InfoRow label="Offer Company" value={c.offerCompany} />
            <InfoRow label="Offer Package" value={c.offerPackage} />
            <InfoRow label="Offer DOJ"     value={fmtDate(c.offerDoj)} />
          </MaybeSection>

          <MaybeSection has={hasExpSummary} title="Relevant Experience Summary" icon="💼">
            <SafeHtml html={c.experienceSummary} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }} />
          </MaybeSection>

          <MaybeSection has={hasNotes} title="Notes" icon="📝">
            {c.notes && (
              <div style={{ marginBottom: c.clientNotes ? 12 : 0 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Internal</span>
                <SafeHtml html={c.notes} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }} />
              </div>
            )}
            {c.clientNotes && (
              <div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Client-facing</span>
                <SafeHtml html={c.clientNotes} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }} />
              </div>
            )}
          </MaybeSection>

        </div>
      )}

      {/* Resume. getFullProfile deliberately omits any signed URL — pre-signed
          links expire, so one minted when this modal opened could already be
          dead. ResumeButton fetches a fresh one on click instead. */}
      {c.resumeName && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 10, marginTop: 16,
        }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, overflowWrap: 'anywhere', minWidth: 0 }}>
            📄 Resume on file: <span style={{ color: 'var(--text-secondary)' }}>{c.resumeName}</span>
          </p>
          {/* Prefer the prop over c.id: `c` may be the candidateFallback subset
              (getFullProfile 403s for some roles), so the id the modal was
              opened WITH is the reliable one. */}
          {(candidateId || c.id) && (
            <ResumeButton
              candidateId={candidateId || c.id}
              resumeName={c.resumeName}
              style={{ flexShrink: 0 }}
            />
          )}
        </div>
      )}

      <div className="flex justify-end" style={{ marginTop: 18 }}>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}
